import {
  Alert,
  ArrowRightIcon,
  Box,
  Button,
  Checkbox,
  InfoIcon,
  Link,
  List,
  ListItem,
  ListItemText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from '@razorpay/blade/components';
import React from 'react';
import { useRunPayrollDataContext } from '../contexts/RunPayrollDataContext';
import pluralize from 'pluralize';
import { routePaths } from 'components/Routes/data';
import { prepareUrlV2 } from 'utils/Urls';
import { format } from 'date-fns';

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onExecute: () => void;
  onCancel: () => void;
};

const PayrollFinalizationAcknowledgmentModal = ({
  isOpen,
  onOpenChange,
  onCancel,
  onExecute,
}: Props): React.ReactElement => {
  const [isChecked, setIsChecked] = React.useState(false);
  const data = useRunPayrollDataContext();
  const skippedEmployees = data.skippedEmployeeCountForFY ?? 0;
  const autoSkippedCount = data.autoSkipCount ?? 0;
  const warningCount = data.negativeNetPayWarningCount ?? 0;
  const negativeSAWarningCount = data.negativeSAWarningCount ?? 0;
  const pfMismatchWarningCount = data.pfMismatchWarningCount ?? 0;
  const totalEmployees = autoSkippedCount + warningCount + negativeSAWarningCount + pfMismatchWarningCount;

  const payrollMonthDate = data?.payrollMonth
    ? new Date(data?.payrollMonth + 'T00:00:00')
    : new Date();

  // Build list items dynamically - only show items with count > 0
  const listItems = [];

  if (autoSkippedCount > 0) {
    listItems.push({
      key: 'auto-skipped',
      text: `Payroll skipped for ${autoSkippedCount} ${pluralize('employee', autoSkippedCount)}`,
    });
  }

  if (warningCount > 0) {
    listItems.push({
      key: 'negative-net-pay',
      text: `Negative net pay for ${warningCount} ${pluralize('employee', warningCount)}`,
    });
  }

  if (negativeSAWarningCount > 0) {
    listItems.push({
      key: 'negative-sa',
      text: `${negativeSAWarningCount} ${pluralize('employee', negativeSAWarningCount)} ${pluralize(
        'has',
        negativeSAWarningCount,
      )} negative special allowance`,
    });
  }

  if (pfMismatchWarningCount > 0) {
    listItems.push({
      key: 'pf-mismatch',
      text: `${pfMismatchWarningCount} ${pluralize('employee', pfMismatchWarningCount)} ${pluralize(
        'has',
        pfMismatchWarningCount,
      )} PF contribution mismatch`,
    });
  }

  const handleDismiss = () => {
    onOpenChange(false);
    setIsChecked(false);
  };

  const handleProceed = () => {
    if (isChecked) {
      onExecute();
      setIsChecked(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      size="small"
      accessibilityLabel="Acknowledgment">
      <ModalHeader title="Acknowledgment" />
      <ModalBody>
        <Box display="flex" flexDirection="column" gap="spacing.5" justifyContent="center">
          {totalEmployees > 0 && (
            <Box
              borderWidth={'thick'}
              borderRadius={'medium'}
              borderColor={'surface.border.primary.muted'}
              testID="warning-employees-alert">
              <Alert
                description={
                  <>
                    <Text size="small" color="surface.text.gray.subtle">
                      Review {totalEmployees} {pluralize('employee', totalEmployees)}
                    </Text>
                    <List variant="unordered" size="small" marginTop="spacing.3">
                      {listItems.map((item) => (
                        <ListItem key={item.key}>
                          <ListItemText color="surface.text.gray.subtle">{item.text}</ListItemText>
                        </ListItem>
                      ))}
                    </List>
                    <Box marginTop="spacing.3">
                      <Button
                        variant="secondary"
                        size="small"
                        iconPosition="right"
                        icon={ArrowRightIcon}
                        onClick={() => {
                          window.open(
                            prepareUrlV2(routePaths.customReport.root, {
                              type: 14,
                              payrollMonth: data.payrollMonth,
                            }),
                            '_blank',
                            'noopener,noreferrer',
                          );
                        }}>
                        View report
                      </Button>
                    </Box>
                  </>
                }
                icon={InfoIcon}
                color="information"
                emphasis="subtle"
                isDismissible={false}
              />
            </Box>
          )}

          {skippedEmployees > 0 && (
            <Box
              borderWidth={'thick'}
              borderRadius={'medium'}
              borderColor={'surface.border.primary.muted'}
              testID="skipped-employees-alert">
              <Alert
                description={
                  <>
                    <Text size="small" color="surface.text.gray.subtle">
                      Skip {skippedEmployees} Pending {pluralize('payroll', skippedEmployees)}
                    </Text>
                    <Text marginTop="spacing.3" size="small" color="surface.text.gray.subtle">
                      Once {format(payrollMonthDate, 'MMMM yyyy')} payroll is run, previous pending
                      payroll cannot be executed.
                    </Text>
                    <Box marginTop="spacing.3">
                      <Button
                        variant="secondary"
                        size="small"
                        iconPosition="right"
                        icon={ArrowRightIcon}
                        onClick={() => {
                          window.open(
                            prepareUrlV2(routePaths.customReport.root, {
                              type: 14,
                              payrollMonth: data.payrollMonth,
                            }),
                            '_blank',
                            'noopener,noreferrer',
                          );
                        }}>
                        View report
                      </Button>
                    </Box>
                  </>
                }
                icon={InfoIcon}
                color="information"
                emphasis="subtle"
                isDismissible={false}
              />
            </Box>
          )}
          <Checkbox
            size="small"
            marginTop={'spacing.3'}
            isChecked={isChecked}
            onChange={() => setIsChecked(!isChecked)}>
            I acknowledge and have reviewed the report
          </Checkbox>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" flexDirection="row" gap="spacing.5" justifyContent="flex-end">
          <Button variant="tertiary" size="medium" isFullWidth onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            isFullWidth
            isDisabled={!isChecked}
            onClick={handleProceed}>
            Proceed
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default PayrollFinalizationAcknowledgmentModal;
